import { c as _c } from "react/compiler-runtime";
import request from '@onefootprint/request';
import { useQuery } from '@tanstack/react-query';
import type { AuthHeaders } from '@/hooks/use-session';
import useSession from '@/hooks/use-session';
export type PercyChatSessionModelCost = {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  totalTokens: number;
  /** `null` when the backend doesn't know pricing for this model. */
  costUsd: number | null;
};
export type PercyChatSessionCost = {
  models: PercyChatSessionModelCost[];
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheCreationInputTokens: number;
  totalCacheReadInputTokens: number;
  totalTokens: number;
  totalCostUsd: number;
};
export type ChatSessionCostTarget = {
  chatSessionId: string;
} | {
  investigationId: string;
};
const getChatSessionCost = async (authHeaders: AuthHeaders, target: ChatSessionCostTarget) => {
  // `axios-case-converter` snake_cases these to `chat_session_id` / `investigation_id`.
  const params = 'chatSessionId' in target ? {
    chatSessionId: target.chatSessionId
  } : {
    investigationId: target.investigationId
  };
  const {
    data
  } = await request<PercyChatSessionCost>({
    method: 'GET',
    url: 'private/chat_sessions/cost',
    headers: authHeaders,
    params
  });
  return data;
};

/**
 * Risk-ops-firm-employee-only: fetches the token usage and estimated USD cost of
 * a chat session or investigation from `GET /private/chat_sessions/cost`. Pass
 * `enabled: false` to defer the request until the cost bar is toggled open.
 */
const useChatSessionCost = (target, enabled) => {
  const $ = _c(9);
  const {
    authHeaders,
    data: t0
  } = useSession();
  const {
    user
  } = t0;
  const isRiskOpsFirmEmployee = !!user?.isFirmEmployee && !!user?.isRiskOpsEmployee;
  const key = target && ("chatSessionId" in target ? `chat:${target.chatSessionId}` : `investigation:${target.investigationId}`);
  let t1;
  if ($[0] !== key) {
    t1 = ["chat-session-cost", key];
    $[0] = key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== authHeaders || $[3] !== target) {
    t2 = () => getChatSessionCost(authHeaders, target as ChatSessionCostTarget);
    $[2] = authHeaders;
    $[3] = target;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = enabled && isRiskOpsFirmEmployee && !!target;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
};
export default useChatSessionCost;